import { env } from './env';

export type GetAccessToken = () => Promise<string>;
export type GetActiveCompanyId = () => string | null;

export interface RequestOpts {
  headers?: Record<string, string>;
  signal?: AbortSignal;
  idempotencyKey?: string;
  /** No enviar X-Active-Company (p. ej. /me: resuelve las empresas del usuario y no
   *  debe fallar si la empresa elegida dejó de ser suya). */
  omitActiveCompany?: boolean;
}

export interface ApiClient {
  get<T>(path: string, opts?: RequestOpts): Promise<T>;
  post<T>(path: string, body: unknown, opts?: RequestOpts): Promise<T>;
  put<T>(path: string, body: unknown, opts?: RequestOpts): Promise<T>;
  patch<T>(path: string, body: unknown, opts?: RequestOpts): Promise<T>;
  delete<T>(path: string, opts?: RequestOpts): Promise<T>;
  /** GET de un binario (p. ej. un PDF): el cuerpo como Blob y los headers expuestos. */
  getBlob(path: string, opts?: RequestOpts): Promise<BlobResponse>;
}

export interface BlobResponse {
  blob: Blob;
  headers: Headers;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly correlationId?: string,
    public readonly meta?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** La petición no llegó al servidor (sin conexión, DNS, CORS): fetch rechazó antes de una
 *  respuesta. Se separa de los errores de programación, que también son TypeError. */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('No se pudo conectar con el servidor', { cause });
    this.name = 'NetworkError';
  }
}

// Mensajes de una falla de fetch: Chrome, Firefox, Safari y el timeout del worker de Auth0.
const FETCH_FAILURE =
  /failed to fetch|networkerror when attempting|load failed|timeout when executing 'fetch'/i;

/** Falla al obtener el token: renovarlo sale a la red (Auth0 con refresh tokens). Auth0 usa
 *  un Web Worker y devuelve la falla de fetch como `new Error(mensaje)`; sin worker relanza el
 *  TypeError de fetch. Esas, o el navegador sin conexión, son NetworkError. Un rechazo propio
 *  de Auth0 (login_required, consent_required) se propaga tal cual. */
function tokenFailure(err: unknown): unknown {
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
  const fetchFailed = err instanceof Error && FETCH_FAILURE.test(err.message);
  return offline || fetchFailed || err instanceof TypeError ? new NetworkError(err) : err;
}

/** Motivo del rechazo que manda el backend (`error.meta.reason`), si lo hay. */
export function apiErrorReason(err: unknown): string | undefined {
  if (!(err instanceof ApiError)) {
    return undefined;
  }
  const reason = err.meta?.['reason'];
  return typeof reason === 'string' ? reason : undefined;
}

const STEP_UP_REASON = 'step_up_required';

/** La firma exige una autenticación reciente (step-up): hay que volver a iniciar sesión. */
export function isStepUpRequired(err: unknown): boolean {
  return err instanceof ApiError && err.status === 401 && err.meta?.['reason'] === STEP_UP_REASON;
}

interface ApiErrorPayload {
  error?: {
    code: string;
    message: string;
    correlation_id?: string;
    meta?: Record<string, unknown>;
  };
}

export function createApiClient(
  getAccessToken: GetAccessToken,
  onUnauthorized?: () => void,
  getActiveCompanyId?: GetActiveCompanyId,
): ApiClient {
  /** Envía la petición con token y empresa activa; un status no-OK se vuelve ApiError. */
  async function send(
    method: string,
    path: string,
    body: unknown,
    opts: RequestOpts,
    accept: string,
  ): Promise<Response> {
    let token: string;
    try {
      token = await getAccessToken();
    } catch (err) {
      throw tokenFailure(err);
    }
    const activeCompanyId = opts.omitActiveCompany ? null : (getActiveCompanyId?.() ?? null);
    const headers: Record<string, string> = {
      accept,
      ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
      ...(opts.idempotencyKey ? { 'idempotency-key': opts.idempotencyKey } : {}),
      // Empresa activa del eje multiempresa (el backend valida pertenencia).
      ...(activeCompanyId ? { 'x-active-company': activeCompanyId } : {}),
      ...opts.headers,
      authorization: `Bearer ${token}`,
    };

    const url = `${env.VITE_API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
    let res: Response;
    try {
      res = await fetch(url, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal: opts.signal,
      });
    } catch (err) {
      // Una cancelación (AbortError) se propaga tal cual: no es una falla de red.
      if (err instanceof DOMException && err.name === 'AbortError') {
        throw err;
      }
      throw new NetworkError(err);
    }

    if (!res.ok) {
      let errPayload: ApiErrorPayload = {};
      try {
        errPayload = (await res.json()) as ApiErrorPayload;
      } catch {
        // respuesta sin cuerpo JSON — se usa statusText
      }
      // El 401 de re-autenticación para firmar lo resuelve la pantalla con un login
      // explícito (prompt=login); el login silencioso reusaría la sesión de Auth0 sin
      // renovar auth_time y volvería a fallar en bucle.
      const stepUp = errPayload.error?.meta?.['reason'] === STEP_UP_REASON;
      if (res.status === 401 && onUnauthorized && !stepUp) {
        onUnauthorized();
      }
      throw new ApiError(
        res.status,
        errPayload.error?.code ?? 'UNKNOWN',
        errPayload.error?.message ?? res.statusText,
        errPayload.error?.correlation_id,
        errPayload.error?.meta,
      );
    }
    return res;
  }

  async function request<T>(
    method: string,
    path: string,
    body?: unknown,
    opts: RequestOpts = {},
  ): Promise<T> {
    const res = await send(method, path, body, opts, 'application/json');
    if (res.status === 204) {
      return undefined as T;
    }
    return (await res.json()) as T;
  }

  return {
    get: <T>(path: string, opts?: RequestOpts) => request<T>('GET', path, undefined, opts),
    post: <T>(path: string, body: unknown, opts?: RequestOpts) =>
      request<T>('POST', path, body, opts),
    put: <T>(path: string, body: unknown, opts?: RequestOpts) =>
      request<T>('PUT', path, body, opts),
    patch: <T>(path: string, body: unknown, opts?: RequestOpts) =>
      request<T>('PATCH', path, body, opts),
    delete: <T>(path: string, opts?: RequestOpts) => request<T>('DELETE', path, undefined, opts),
    getBlob: async (path: string, opts: RequestOpts = {}) => {
      const res = await send(
        'GET',
        path,
        undefined,
        opts,
        'application/pdf, text/csv, application/json',
      );
      return { blob: await res.blob(), headers: res.headers };
    },
  };
}
